// New w-artifact custom element that loads page or artifact HTML in sandboxed iframe
// See docs in iframe-proxy.md

import type { PageApi } from '@shared/api/pages/types'
import type { Content, Endpoints } from '@shared/api/web-components/make-artifact-api'
import type { Artifact } from '@shared/types/artifacts'
import { loadESMComponentByPackageJSON, loadWebPageByPackageJson } from '@shared/api/web-components'
import { replaceProxyUrls } from '@shared/api/web-components/constants'
import {
  defaultArtifactEndpoint,
  defaultPageEndpoints,
  makeArtifactApi,
} from '@shared/api/web-components/make-artifact-api'
import { IFRAME_MESSAGE_TYPE, IFrameMessageType } from '@shared/iframe-messages'
import { ArtifactStatus, ArtifactType } from '@shared/types/artifacts'
import { uuid } from '@shared/util/uuid'
import { isWippyPackageComponent, isWippyPackageESMModule, isWippyPackageWebPage } from '@shared/wippy-package/wippy-package'

// Kick off the heavy markdown module (shiki + Oniguruma WASM, ~9 MB) as soon
// as this module is loaded so it's ready by the time an artifact actually
// needs it. Loaded via external import map entry (see web-components/constants.ts)
// so its code does not inline into proxy.js — UMD output can't code-split.
// The import map is injected before proxy.js in the srcdoc (ensured by
// processWebPage) so the bare specifier resolves via the document's import map.
const parseMarkdownIframePromise = import('@wippy-fe/markdown-iframe').then(m => m.parseMarkdownIframe)

// Surface loading failures through the proxy logger once the instance exists.
// Attaching a handler also marks the original promise as handled, so awaiting
// it later in renderMarkdownIframe still throws but no unhandled rejection.
parseMarkdownIframePromise.catch((err) => {
  window.getWippyApi?.()
    .then(inst => inst.logger.captureException(err, {
      source: '@wippy-fe/markdown-iframe',
      component: 'w-artifact',
    }))
    .catch(() => {
      // Logger itself unavailable — fall back to console so the error is not lost.
      console.error('[w-artifact] Failed to load @wippy-fe/markdown-iframe:', err)
    })
})

// Helpers to convert attr value to boolean
function attrBoolean(val: string | null | undefined): boolean {
  if (val === null || val === undefined)
    return false
  if (val === '')
    return true
  return val === 'true'
}

interface SubscriptionRecord {
  topic: string
  off: () => void
}

function generateStyle(tagName: string, fillContainer = false) {
  const heightRule = fillContainer ? 'height:100%;' : ''
  return `
      :host { display:block; ${heightRule} }
      .loader { display:flex; align-items:center; justify-content:center; padding:1rem; font-size:14px; color:#666; ${heightRule} }
      .error { color: red; padding:1rem; }
      ${tagName} { display:block; width:100%; ${heightRule} border:0; }
    `
}

const mainProps = ['id', 'type', 'auto-height', 'url', 'sub-path', 'nav-owner']

class WArtifactElement extends HTMLElement {
  static get observedAttributes() {
    return [...mainProps]
  }

  /** element attributes */
  private artifactId: string | null = null
  private artifactType: 'page' | 'artifact' = 'artifact'
  private autoHeight = false
  private customUrl: string | null = null
  private subPath: string | null = null
  private navOwner = false

  /** internals */
  private shadow: ShadowRoot
  private element: HTMLElement | null = null
  private loadingEl!: HTMLElement
  private errorEl!: HTMLElement
  private messageListener?: (e: MessageEvent) => void
  private subscriptions: Record<string, SubscriptionRecord> = {}
  private layoutEventListener?: (e: MessageEvent) => void
  /**
   * Ref-count of inner iframe subscribers. The inner child proxy sends
   * exactly one CmdLayoutSubscribe per `createLayoutAPI()` call — but
   * HMR / re-boot / re-mount scenarios can produce multiple. Counting
   * lets the re-subscribe path work: after an unsub, subsequent
   * subscribes correctly re-establish the upward chain.
   */
  private layoutSubCount = 0
  private styleEl!: HTMLStyleElement
  private otherProps = new Map<string, string | null>()
  private endpoints: Endpoints = defaultArtifactEndpoint
  private observer: MutationObserver | null = null
  private iframe: HTMLIFrameElement | null = null
  private loading = false

  constructor() {
    super()
    this.shadow = this.attachShadow({ mode: 'open' })
    // Basic styles
    this.styleEl = document.createElement('style')
    this.styleEl.textContent = generateStyle('iframe', !this.autoHeight)
    this.shadow.appendChild(this.styleEl)

    // Loading placeholder
    this.loadingEl = document.createElement('div')
    this.loadingEl.className = 'loader'
    this.loadingEl.textContent = 'Loading...'
    this.loadingEl.setAttribute('part', 'loader')
    this.shadow.appendChild(this.loadingEl)

    // Error placeholder
    this.errorEl = document.createElement('div')
    this.errorEl.className = 'error'
    this.errorEl.style.display = 'none'
    this.errorEl.setAttribute('part', 'error')
    this.shadow.appendChild(this.errorEl)
  }

  connectedCallback() {
    this.updateInternalState()
    this.load()
  }

  disconnectedCallback() {
    this.emitNavOwnerRelease()
    this.cleanup()
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (!this.isConnected)
      return
    // Nav-owner: once loading/loaded, never reload. Only forward sub-path changes.
    if (this.navOwner && (this.iframe || this.loading)) {
      if (name === 'sub-path' && this.iframe && oldValue !== null && newValue !== oldValue) {
        this.subPath = newValue
        const path = newValue || '/'
        this.iframe.contentWindow?.postMessage(JSON.stringify({
          type: IFRAME_MESSAGE_TYPE,
          action: IFrameMessageType.UrlWasUpdatedInParent,
          path: path.startsWith('/') ? path : `/${path}`,
        }), '*')
      }
      return
    }
    this.updateInternalState()
    this.load()
  }

  /** read attributes into fields */
  private updateInternalState() {
    this.artifactId = this.getAttribute('id')
    const typeAttr = this.getAttribute('type')
    this.artifactType = (typeAttr === 'page' ? 'page' : 'artifact')
    this.autoHeight = attrBoolean(this.getAttribute('auto-height'))
    this.customUrl = this.getAttribute('url')
    this.subPath = this.getAttribute('sub-path')
    this.navOwner = this.hasAttribute('nav-owner')
    const existingProps = this.getAttributeNames()
    const prevProps = Object.keys(this.otherProps)

    // Remove previous props
    prevProps.forEach((prop) => {
      if (!existingProps.includes(prop)) {
        this.otherProps.delete(prop)
      }
    })
    // Update current props
    existingProps.forEach((prop) => {
      if (mainProps.includes(prop))
        return // skip main props
      const value = this.getAttribute(prop)
      this.otherProps.set(prop, value)
    })

    const endPoints = this.artifactType === 'page'
      ? { ...defaultPageEndpoints }
      : { ...defaultArtifactEndpoint }
    if (this.customUrl) {
      endPoints.content = () => this.customUrl!
    }
    this.endpoints = endPoints
  }

  private cleanup() {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener)
      this.messageListener = undefined
    }
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    Object.values(this.subscriptions).forEach(s => s.off())
    this.subscriptions = {}
    if (this.layoutEventListener) {
      window.removeEventListener('message', this.layoutEventListener)
      this.layoutEventListener = undefined
    }
    if (this.layoutSubCount > 0) {
      this.layoutSubCount = 0
      try {
        window.parent.postMessage(
          JSON.stringify({ type: IFRAME_MESSAGE_TYPE, action: IFrameMessageType.CmdLayoutUnsubscribe }),
          '*',
        )
      }
      catch {
        // parent may already be gone
      }
    }
    this.iframe = null
  }

  private emitNavOwnerClaim() {
    if (!this.navOwner || !this.artifactId)
      return
    window.parent.postMessage(JSON.stringify({
      type: IFRAME_MESSAGE_TYPE,
      action: IFrameMessageType.CmdClaimNavOwner,
      sourceId: this.artifactId,
    }), '*')
  }

  private emitNavOwnerRelease() {
    if (!this.navOwner || !this.artifactId)
      return
    window.parent.postMessage(JSON.stringify({
      type: IFRAME_MESSAGE_TYPE,
      action: IFrameMessageType.CmdReleaseNavOwner,
      sourceId: this.artifactId,
    }), '*')
  }

  private async loadMeta(): Promise<Artifact> {
    if (this.customUrl) {
      return {
        uuid: this.customUrl,
        title: this.customUrl,
        content_type: 'text/html',
        content_version: '',
        type: ArtifactType.INLINE_INTERACTIVE,
        status: ArtifactStatus.IDLE,
      }
    }
    const { api } = await window.getWippyApi()
    if (this.artifactType === 'page') {
      const response = await makeArtifactApi<PageApi.Page>(api, defaultPageEndpoints).getMeta(this.artifactId || '')
      return {
        uuid: response.id,
        title: response.title || 'Page',
        content_type: response.content_type || 'text/html',
        content_version: response.content_version || '',
        type: ArtifactType.INLINE_INTERACTIVE,
        status: ArtifactStatus.IDLE,
      }
    }
    else {
      const response = await makeArtifactApi<Artifact>(api, defaultArtifactEndpoint).getMeta(this.artifactId || '')
      return {
        ...response,
        type: ArtifactType.INLINE_INTERACTIVE,
        status: response.status || ArtifactStatus.IDLE,
      }
    }
  }

  /** fetch html content and render iframe */
  private async load() {
    // guard
    if (!this.customUrl && !this.artifactId)
      return

    // Remove previous stuff
    this.loading = true
    this.cleanup()
    if (this.element) {
      this.shadow.removeChild(this.element)
      this.element = null
    }

    // Show loading
    this.setStatus('loading')
    this.loadingEl.style.display = 'flex'
    this.errorEl.style.display = 'none'
    this.dispatchEvent(new CustomEvent('loading'))

    try {
      const [meta, content] = await Promise.all([this.loadMeta(), this.loadContent()])
      await this.render(meta, content)

      this.setStatus('ready')
      this.loadingEl.style.display = 'none'
      this.dispatchEvent(new CustomEvent('load'))
    }
    catch (err) {
      const message = (err as Error).message || 'Failed to load'
      this.setStatus('error')
      this.errorEl.textContent = message
      this.loadingEl.style.display = 'none'
      this.errorEl.style.display = 'block'
      this.dispatchEvent(new CustomEvent('error', { detail: err }))
    }

    this.loading = false
    this.setupAttributeObserver()
  }

  private async loadContent() {
    const { api } = await window.getWippyApi()
    return await makeArtifactApi(api, this.endpoints).getContent(this.artifactId || '')
  }

  private async render(meta: Artifact, content: Content) {
    const contentType = content.contentType || meta.content_type || 'text/html'
    if (contentType === 'text/markdown')
      await this.renderMarkdownIframe(content)
    else if (contentType === 'application/json')
      await this.renderComponent(content)
    else
      await this.renderHtmlIframe(content)
  }

  private async renderComponent(content: Content) {
    const { api } = await window.getWippyApi()
    if (typeof content.data !== 'object') {
      throw new TypeError(`Invalid content type for component: expected object, got ${typeof content.data}`)
    }
    const packageJson = content.data
    if (isWippyPackageComponent(packageJson)) {
      if (packageJson.props) {
        for (const [key, value] of Object.entries(packageJson.props))
          this.otherProps.set(key, typeof value === 'string' ? value : JSON.stringify(value))
      }
      await this.renderDirectTag(packageJson.wippy.tagName)
    }
    else if (isWippyPackageESMModule(packageJson)) {
      if (packageJson.props) {
        for (const [key, value] of Object.entries(packageJson.props))
          this.otherProps.set(key, typeof value === 'string' ? value : JSON.stringify(value))
      }
      const tagName = await loadESMComponentByPackageJSON({ uuid: this.artifactId || '', packageJson }, api)
      this.renderTag(tagName)
    }
    else if (isWippyPackageWebPage(packageJson)) {
      const srcdoc = await loadWebPageByPackageJson({ uuid: this.artifactId || '', packageJson, initialRoute: this.subPath || undefined }, api)
      this.renderIframe(srcdoc)
    }
  }

  private async renderMarkdownIframe(content: Content) {
    if (typeof content.data !== 'string') {
      throw new TypeError(`Invalid content type for Markdown: expected string, got ${typeof content.data}`)
    }
    const parseMarkdownIframe = await parseMarkdownIframePromise
    const html = await parseMarkdownIframe(content.data)
    this.renderIframe(html)
  }

  private async renderHtmlIframe(content: Content) {
    if (typeof content.data !== 'string') {
      throw new TypeError(`Invalid content type for HTML: expected string, got ${typeof content.data}`)
    }
    this.renderIframe(content.data)
  }

  private renderIframe(html: string) {
    // Replace deprecated front.wippy.ai proxy URLs with current proxy URL
    html = replaceProxyUrls(html)

    this.styleEl.textContent = generateStyle('iframe', !this.autoHeight)
    const iframe = document.createElement('iframe')
    iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-forms allow-popups')
    iframe.style.width = '100%'
    iframe.srcdoc = html
    iframe.setAttribute('part', 'frame')

    // When loaded, if auto-height disabled we dispatch load anyway. (load event already dispatched in load())
    iframe.addEventListener('load', () => {
      // nothing extra here; size adjustments will come through message event
    })

    this.shadow.appendChild(iframe)
    this.element = iframe
    this.iframe = iframe

    this.setupMessageBridge()
    this.emitNavOwnerClaim()
  }

  private renderTag(tagName: string) {
    this.styleEl.textContent = generateStyle(tagName, !this.autoHeight)
    const childEl = document.createElement(tagName)
    this.otherProps.forEach((value, key) => {
      childEl.setAttribute(key, value || '')
    })
    childEl.setAttribute('part', 'frame')

    this.shadow.appendChild(childEl)
    this.element = childEl
  }

  private updateTagProps() {
    if (!this.element)
      return

    // Only sync props for non-iframe elements (web component tags).
    // Iframe elements have their attributes (srcdoc, sandbox, style) set by
    // renderIframe — stripping them here would destroy the iframe content.
    if (!(this.element instanceof HTMLIFrameElement)) {
      const existingAttributes = this.element.getAttributeNames()
      for (const attr of existingAttributes) {
        if (attr !== 'part' && !this.otherProps.has(attr))
          this.element?.removeAttribute(attr)
      }
    }

    // Set current props if they are different from old one
    this.otherProps.forEach((value, key) => {
      if (this.element?.getAttribute(key) !== value) {
        if (value === null || value === undefined) {
          this.element?.removeAttribute(key)
        }
        else {
          this.element?.setAttribute(key, value)
        }
      }
    })
  }

  private async renderDirectTag(tagName: string) {
    if (!customElements.get(tagName)) {
      const timeout = 10_000
      let timer: ReturnType<typeof setTimeout>
      await Promise.race([
        customElements.whenDefined(tagName),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new Error(`<${tagName}> not defined within ${timeout}ms`)), timeout)
        }),
      ]).finally(() => clearTimeout(timer))
    }
    this.renderTag(tagName)
  }

  private setupAttributeObserver() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          this.updateInternalState()
          this.updateTagProps()
        }
      })
    })
    this.observer.observe(this, { attributes: true })
  }

  private async setupMessageBridge() {
    if (!this.element || !(this.element instanceof HTMLIFrameElement))
      return

    const sourceWindow = this.element.contentWindow
    if (!sourceWindow)
      return

    // Setup message listener
    this.messageListener = async (event: MessageEvent) => {
      if (event.source !== sourceWindow) {
        return
      }
      if (typeof event.data !== 'string') {
        return
      }

      let data: any
      try {
        data = JSON.parse(event.data)
      }
      // eslint-disable-next-line unused-imports/no-unused-vars
      catch (_e) {
        return
      }
      if (data.type !== IFRAME_MESSAGE_TYPE) {
        return
      }

      const action: IFrameMessageType = data.action
      const rest = { ...data }
      delete rest.type
      delete rest.action

      switch (action) {
        case IFrameMessageType.CmdBodySize:
          if (this.autoHeight && this.element) {
            const { height } = rest
            if (typeof height === 'number') {
              this.element.style.height = `${height}px`
            }
          }
          break
        case IFrameMessageType.CmdStartChat: {
          const { start_token, ...options } = rest
          if (!start_token)
            break
          const { host } = await window.getWippyApi()
          host.startChat(start_token, options)
          break
        }
        case IFrameMessageType.CmdOpenSession: {
          const { uuid, ...options } = rest
          if (!uuid)
            break
          const { host } = await window.getWippyApi()
          host.openSession(uuid, options)
          break
        }
        case IFrameMessageType.CmdOpenArtifact: {
          const { uuid, ...options } = rest
          if (!uuid)
            break
          const { host } = await window.getWippyApi()
          host.openArtifact(uuid, options)
          break
        }
        case IFrameMessageType.CmdNavigate: {
          const { url } = rest
          if (url) {
            const { host } = await window.getWippyApi()
            host.navigate(url)
          }
          break
        }
        case IFrameMessageType.CmdRouteChanged: {
          if (this.navOwner) {
            // Nav-owner child navigated — dispatch a DOM event so the parent
            // Vue component can update its own router (it knows its own route
            // prefix and can compose the full path).
            this.dispatchEvent(new CustomEvent('nav-owner-route', {
              detail: { path: rest.internalRoute || '/', navId: rest.navId },
              bubbles: true,
              composed: true,
            }))
          }
          // Non-nav-owner artifacts: route changes are nav-isolated (no relay)
          break
        }
        case IFrameMessageType.CmdHandleError: {
          const { code = 'other', error } = rest
          const { host } = await window.getWippyApi()
          host.handleError(code, error)
          break
        }
        case IFrameMessageType.CmdShowToast: {
          const { host } = await window.getWippyApi()
          host.toast(rest)
          break
        }
        case IFrameMessageType.CmdShowConfirm: {
          const { uuid: confirmUuid, ...options } = rest
          if (!confirmUuid)
            break
          const { host } = await window.getWippyApi()
          try {
            const res = await host.confirm(options)
            sourceWindow.postMessage(JSON.stringify({ type: IFRAME_MESSAGE_TYPE, action: IFrameMessageType.OnConfirmResult, uuid: confirmUuid, result: res }), '*')
          }
          // eslint-disable-next-line unused-imports/no-unused-vars
          catch (_e) {
            sourceWindow.postMessage(JSON.stringify({ type: IFRAME_MESSAGE_TYPE, action: IFrameMessageType.OnConfirmResult, uuid: confirmUuid, result: false }), '*')
          }
          break
        }
        case IFrameMessageType.CmdSetContext: {
          const { context, sessionUUID } = rest
          if (!context) {
            break
          }
          ;(await window.getWippyApi()).host.setContext(context, sessionUUID, {
            uuid: this.artifactId || this.customUrl || '',
            type: this.artifactType,
          })
          break
        }
        case IFrameMessageType.CmdLogout: {
          (await window.getWippyApi()).host.logout()
          break
        }
        case IFrameMessageType.CmdSubscribe: {
          const topic = rest.topic
          if (!topic)
            break
          if (this.subscriptions[topic])
            return // already subscribed
          const { on } = await window.getWippyApi()
          const off = on(topic as string, (message: any) => {
            sourceWindow.postMessage(JSON.stringify({ type: IFRAME_MESSAGE_TYPE, action: IFrameMessageType.OnSubscription, message }), '*')
          }) as unknown as () => void
          this.subscriptions[topic] = { topic, off }
          break
        }
        case IFrameMessageType.CmdUnSubscribe: {
          const topic = rest.topic
          if (!topic)
            break
          const rec = this.subscriptions[topic]
          if (rec) {
            rec.off()
            delete this.subscriptions[topic]
          }
          break
        }
        case IFrameMessageType.GetConfig: {
          const { config } = await window.getWippyApi()
          const overriddenPath = this.subPath ?? config.context?.route
          const parentDepth = config.context?.nestingDepth ?? 0
          sourceWindow.postMessage(JSON.stringify({
            ...config,
            selfPageId: this.artifactId,
            type: IFRAME_MESSAGE_TYPE,
            action: IFrameMessageType.SetConfig,
            context: {
              ...config.context,
              resourceId: this.artifactId,
              resourceType: this.artifactType,
              parentResourceId: config.context?.resourceId,
              nestingDepth: parentDepth + 1,
              isNavOwner: this.navOwner || undefined,
              route: overriddenPath,
            },
          }), '*')
          break
        }
        // --- WebSocket send relay ---
        case IFrameMessageType.CmdWsSend: {
          if (rest.uuid) {
            // UUID-tracked: relay with outer UUID, wait for response, send back with inner UUID
            const innerUuid = rest.uuid
            const outerUuid = uuid()
            window.parent.postMessage(JSON.stringify({
              type: IFRAME_MESSAGE_TYPE,
              action,
              command: rest.command,
              uuid: outerUuid,
            }), '*')
            let relayTimeout: ReturnType<typeof setTimeout>
            const responseHandler = (respEvent: MessageEvent) => {
              if (typeof respEvent.data !== 'string')
                return
              let respData: any
              try {
                respData = JSON.parse(respEvent.data)
              }
              // eslint-disable-next-line unused-imports/no-unused-vars
              catch (_e) {
                return
              }
              if (
                respData.type === IFRAME_MESSAGE_TYPE
                && respData.action === IFrameMessageType.OnWsResult
                && respData.uuid === outerUuid
              ) {
                clearTimeout(relayTimeout)
                window.removeEventListener('message', responseHandler)
                sourceWindow.postMessage(JSON.stringify({
                  type: IFRAME_MESSAGE_TYPE,
                  action: IFrameMessageType.OnWsResult,
                  uuid: innerUuid,
                  result: respData.result,
                  error: respData.error,
                }), '*')
              }
            }
            window.addEventListener('message', responseHandler)
            relayTimeout = setTimeout(() => {
              window.removeEventListener('message', responseHandler)
              sourceWindow.postMessage(JSON.stringify({
                type: IFRAME_MESSAGE_TYPE,
                action: IFrameMessageType.OnWsResult,
                uuid: innerUuid,
                error: 'WebSocket relay timed out',
              }), '*')
            }, 30_000)
          }
          else {
            // Fire-and-forget
            window.parent.postMessage(JSON.stringify({
              type: IFRAME_MESSAGE_TYPE,
              action,
              ...rest,
            }), '*')
          }
          break
        }
        // --- Log relay (fire-and-forget) ---
        case IFrameMessageType.CmdLog: {
          window.parent.postMessage(JSON.stringify({
            type: IFRAME_MESSAGE_TYPE,
            action,
            ...rest,
            source: {
              ...rest.source,
              nestingDepth: (rest.source?.nestingDepth ?? 0) + 1,
            },
          }), '*')
          break
        }
        // --- State relay (fire-and-forget) ---
        case IFrameMessageType.CmdStateSet:
        case IFrameMessageType.CmdStateRemove:
        case IFrameMessageType.CmdStateClear: {
          window.parent.postMessage(JSON.stringify({
            type: IFRAME_MESSAGE_TYPE,
            action,
            ...rest,
            scope: rest.scope || this.artifactId,
          }), '*')
          break
        }
        // --- State relay (UUID-tracked) ---
        case IFrameMessageType.CmdStateGet:
        case IFrameMessageType.CmdStateGetAll: {
          const { uuid: innerUuid, ...stateRest } = rest
          if (!innerUuid)
            break
          // Generate new UUID for host request
          const outerUuid = uuid()
          // Forward to host with outer UUID
          window.parent.postMessage(JSON.stringify({
            type: IFRAME_MESSAGE_TYPE,
            action,
            ...stateRest,
            uuid: outerUuid,
            scope: rest.scope || this.artifactId,
          }), '*')
          // Listen for host response and relay back with original UUID
          let relayTimeout: ReturnType<typeof setTimeout>
          const responseHandler = (respEvent: MessageEvent) => {
            if (typeof respEvent.data !== 'string')
              return
            let respData: any
            try {
              respData = JSON.parse(respEvent.data)
            }
            // eslint-disable-next-line unused-imports/no-unused-vars
            catch (_e) {
              return
            }
            if (
              respData.type === IFRAME_MESSAGE_TYPE
              && respData.action === IFrameMessageType.OnStateResult
              && respData.uuid === outerUuid
            ) {
              clearTimeout(relayTimeout)
              window.removeEventListener('message', responseHandler)
              sourceWindow.postMessage(JSON.stringify({
                type: IFRAME_MESSAGE_TYPE,
                action: IFrameMessageType.OnStateResult,
                uuid: innerUuid,
                value: respData.value,
              }), '*')
            }
          }
          window.addEventListener('message', responseHandler)
          relayTimeout = setTimeout(() => {
            window.removeEventListener('message', responseHandler)
          }, 10_000)
          break
        }
        // --- Layout relay (fire-and-forget mutations) ---
        // --- Title relay (fire-and-forget) ---
        // Title propagation is documented as automatic (CLAUDE.md) — child
        // iframe `document.title` change → proxy MutationObserver →
        // CmdTitleChanged → host. For grand-child iframes (nested via
        // <w-artifact>), the message must be relayed up unchanged so the
        // host receives it identically to a single-level child. Without
        // this case, nested titles silently disappear.
        case IFrameMessageType.CmdTitleChanged:
        case IFrameMessageType.CmdLayoutResizePanel:
        case IFrameMessageType.CmdLayoutCollapsePanel:
        case IFrameMessageType.CmdLayoutExpandPanel:
        case IFrameMessageType.CmdLayoutMovePanel:
        case IFrameMessageType.CmdLayoutRemovePanel:
        case IFrameMessageType.CmdLayoutUpdatePanel:
        case IFrameMessageType.CmdLayoutAddFloating:
        case IFrameMessageType.CmdLayoutCloseModal:
        case IFrameMessageType.CmdLayoutRemoveFloating:
        case IFrameMessageType.CmdLayoutBroadcast: {
          window.parent.postMessage(JSON.stringify({
            type: IFRAME_MESSAGE_TYPE,
            action,
            ...rest,
          }), '*')
          break
        }
        // --- Layout event subscription relay (OnLayoutChange / OnLayoutBreakpoint) ---
        case IFrameMessageType.CmdLayoutSubscribe: {
          this.layoutSubCount += 1
          if (this.layoutSubCount > 1)
            break // upward subscription already active
          // First subscribe — open the upward chain.
          window.parent.postMessage(JSON.stringify({
            type: IFRAME_MESSAGE_TYPE,
            action: IFrameMessageType.CmdLayoutSubscribe,
          }), '*')
          // Forward inbound OnLayout* messages from THIS window's parent
          // down to the inner iframe. Cross-nesting delivery — no DOM
          // broadcast involved. Source-filter to `window.parent` so we
          // never echo the inner iframe's own posts back at it (the
          // inner proxy doesn't post OnLayout* today, but defending by
          // source keeps us safe if anyone in the chain ever does).
          this.layoutEventListener = (evt: MessageEvent) => {
            if (evt.source !== window.parent)
              return
            if (typeof evt.data !== 'string')
              return
            let payload: any
            try {
              payload = JSON.parse(evt.data)
            }
            catch {
              return
            }
            if (payload?.type !== IFRAME_MESSAGE_TYPE)
              return
            if (
              payload.action === IFrameMessageType.OnLayoutChange
              || payload.action === IFrameMessageType.OnLayoutPanelChanged
              || payload.action === IFrameMessageType.OnLayoutBreakpoint
              || payload.action === IFrameMessageType.OnLayoutBroadcast
            ) {
              sourceWindow.postMessage(evt.data, '*')
            }
          }
          window.addEventListener('message', this.layoutEventListener)
          break
        }
        case IFrameMessageType.CmdLayoutUnsubscribe: {
          if (this.layoutSubCount <= 0)
            break
          this.layoutSubCount -= 1
          if (this.layoutSubCount > 0)
            break // inner iframe still has active subs — keep upward chain
          // Last unsub — tear down. Remove downward listener FIRST so a
          // queued OnLayout* in flight doesn't get forwarded after the
          // inner said goodbye.
          if (this.layoutEventListener) {
            window.removeEventListener('message', this.layoutEventListener)
            this.layoutEventListener = undefined
          }
          window.parent.postMessage(JSON.stringify({
            type: IFRAME_MESSAGE_TYPE,
            action: IFrameMessageType.CmdLayoutUnsubscribe,
          }), '*')
          break
        }
        default:
          // Unhandled actions
          break
      }
    }

    window.addEventListener('message', this.messageListener)
  }

  private setStatus(status: 'loading' | 'ready' | 'error') {
    this.setAttribute('status', status)
  }
}

// Register element only once
if (!customElements.get('w-artifact')) {
  customElements.define('w-artifact', WArtifactElement)
}
